import { useState } from 'react';
import { FRAME_MAX_ZOOM, usePhotoFrame } from '@limuzin/kit';
import { Icon } from '../material';
import { Button, Sheet, showProblem } from '../widgets';

/**
 * The framing dialog: the photo sits under a circular window it always fills, and the
 * passenger drags and zooms it until the part they want is in the circle. Hands that
 * square to [onFramed] as a JPEG. The same dialog as Lemmo's photo framing, and as
 * `frameAvatar` in the phone apps; the dragging and zooming are `usePhotoFrame`'s.
 */
export function PhotoFrame({
  file,
  title,
  onFramed,
  onClose,
}: {
  file: File;
  title: string;
  onFramed: (framed: File) => void;
  onClose: () => void;
}) {
  const { stage, image, slider, photo, failed, crop } = usePhotoFrame(file);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!photo || saving) return;
    setSaving(true);
    try {
      onFramed(await crop());
    } catch {
      setSaving(false);
      showProblem('Не удалось подготовить фото');
    }
  };

  return (
    <Sheet
      title={title}
      hint="Перетащите фото, чтобы нужная часть оказалась в круге, и подберите масштаб."
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 10 }}>
        <div
          ref={stage}
          className="photo-frame"
          tabIndex={0}
          role="img"
          aria-label="Предпросмотр фото. Перетаскивайте его или двигайте стрелками, масштаб — клавишами + и −."
        >
          {photo && (
            <img
              ref={image}
              className="photo-frame-image"
              src={photo.url}
              alt=""
              draggable={false}
              width={photo.bitmap.width}
              height={photo.bitmap.height}
            />
          )}
          {!photo && (
            <span className="photo-frame-note">
              {failed ? 'Не удалось открыть фото.' : <span className="spinner" />}
            </span>
          )}
        </div>
        <div className="photo-frame-zoom">
          <Icon name="image_outlined" size={16} />
          <input
            ref={slider}
            type="range"
            min={1}
            max={FRAME_MAX_ZOOM}
            step={0.01}
            defaultValue={1}
            disabled={!photo}
            aria-label="Масштаб"
          />
          <Icon name="image_outlined" size={24} />
        </div>
        <Button size="card" busy={saving} disabled={!photo} onClick={() => void save()}>
          Сохранить
        </Button>
        <Button size="card" tone="plain" onClick={onClose}>
          Отмена
        </Button>
      </div>
    </Sheet>
  );
}
